import { useEffect, useRef, useState } from "react";
import { isSafari } from "react-device-detect";
import { Box, Flex, Image, useDisclosure } from "@chakra-ui/react";
import {
  SocialMediaIcons,
  SocialMediaLink,
} from "../../components/navigation/components/SocialMediaIcons";
import { Apollyon } from "./Apollyon";
import { Reccomendations } from "./Reccomendations";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import {
  FunnelContextArea,
  EventContextArea,
} from "../../components/funnel/types";
import { useGetNavigationContext } from "../../hooks/useGetNavigationContext";
import { Creators } from "./Creators";
import { HiHiStudios } from "./HiHIStudios";
import { Hero } from "./Hero";
import { WhoIsValkyrae } from "./WhoIsValkyrae";
import { ValkyraeVideoModal } from "./ValkyraeVideoModal";
import { ValkyraeSubscribeBanner } from "./ValkyraeSubscribeBanner";
import { ValkyraeFooter } from "./ValkyraeFooter";
import { useInView } from "../../hooks/useInView";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useQuery } from "@tanstack/react-query";
import { getComic } from "../../components/slideInMenu/hooks/useSlideInMenu";
import { useEntityCache } from "../../hooks/useEntityCache";
import { useAccountData } from "../../hooks/useAccountData";
import { useNavigateToReaderWithContext } from "../reader/hooks/useNavigateToReaderWithContext";
import { Comic } from "../../types";
import { useContentFunnel } from "../../components/funnel/hooks/useContentFunnel";
import { PromoRibbion } from "./PromoRibbon";
import { NavigationTrigger } from "../reader/types";

const socialMediaLinks: SocialMediaLink[] = [
  {
    platform: "Youtube",
    url: "https://www.youtube.com/@Valkyrae",
  },
  {
    platform: "TikTok",
    url: "https://tiktok.com/@valkyrae?lang=en",
  },
  {
    platform: "X",
    url: "https://twitter.com/Valkyrae",
  },
  {
    platform: "Instagram",
    url: "https://instagram.com/valkyrae",
  },
];
const GET_APPOLYON_QUERY_KEY = "GET_APPOLYON_QUERY_KEY";
export function ValkyraeScreen() {
  const accountData = useAccountData();
  const isMobileBreakpoint = useMobileBreakpoint();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const [triggerFocusPayment, setTriggerFocusPayment] = useState(0);
  const [heroRef, heroVisible] = useInView(0.3, true);
  const [paymentRef, paymentVisible] = useInView(0.3, false);
  const [initialGoldStatus, setInitialGoldStatus] = useState<boolean | null>(
    null,
  );
  const hasSetInitialStatus = useRef(false);
  const [activeFunnel, setActiveFunnel] = useState<FUNNEL_TYPES | undefined>(
    FUNNEL_TYPES.subscription,
  );
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();

  useFeatureFlag("valkyrae_landing");

  const { saveEntityToCache } = useEntityCache();
  const { data: comicData } = useQuery({
    queryKey: [GET_APPOLYON_QUERY_KEY],
    queryFn: async () => {
      const response = await getComic(
        import.meta.env.PROD ? "apollyon" : "test-comic",
      );
      const comic = response?.data.payload.results;
      if (comic) {
        saveEntityToCache(comic);
        saveEntityToCache(comic.artist);
      }
      return comic;
    },
  });

  useEffect(() => {
    // Only set once when accountData first loads (not during authentication)
    if (accountData && !hasSetInitialStatus.current) {
      setInitialGoldStatus(!!accountData.user?.gold);
      hasSetInitialStatus.current = true;
    }
  }, [accountData]);

  // Reset state when user logs out
  useEffect(() => {
    if (!accountData?.user) {
      setInitialGoldStatus(null);
      hasSetInitialStatus.current = false;
    }
  }, [accountData?.user]);

  useEffect(() => {
    if (isModalOpen) {
      window.document.documentElement.style.overflow = "hidden";
      window.document.documentElement.style.scrollbarGutter = "stable";
    } else {
      window.document.documentElement.style.overflow = "auto";
    }
  }, [isModalOpen]);

  const focusPayment = () => {
    setTriggerFocusPayment((prev) => prev + 1);

    if (isSafari) {
      setTimeout(() => {
        const container = document.querySelector(
          "[data-payment-section]",
        ) as HTMLElement;
        if (container) {
          container.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  };

  const isUserGold = accountData?.user?.gold;
  const showSubscribeBanner = !heroVisible && !paymentVisible && !isUserGold;
  const shouldHideFunnel =
    initialGoldStatus &&
    activeFunnel !== FUNNEL_TYPES.congrats &&
    activeFunnel !== FUNNEL_TYPES.goldAccess;
  const shouldHideRibbon =
    shouldHideFunnel || (isSmallBreakpoint && !heroVisible);

  return (
    <Flex direction="column" width="100%">
      {shouldHideRibbon ? null : <PromoRibbion focusPayment={focusPayment} />}
      <Flex
        as="main"
        role="main"
        position="relative"
        width="100%"
        flexDirection="column"
        alignItems="center"
        paddingBottom="40px"
        backgroundImage={`url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hero_glow_v2.svg)`}
        backgroundRepeat="no-repeat"
        backgroundPosition={isMobileBreakpoint ? "center top" : "center top"}
        overflowX="hidden"
        aria-label="Valkyrae's comic world - featuring Apollyon"
      >
        <ValkyraeVideoModal isOpen={isModalOpen} onClose={closeModal} />
        <Hero
          heroRef={heroRef as React.RefObject<HTMLDivElement>}
          handleCta={focusPayment}
          handleCta2={openModal}
        />
        <Flex
          flexDirection="column"
          gap="88px"
          px="24px"
          width="100%"
          maxWidth="960px"
          alignItems="center"
        >
          <Flex
            as="section"
            flexDirection="column"
            gap="40px"
            width="100%"
            alignItems="center"
            aria-labelledby="valkyrae-intro"
          >
            <WhoIsValkyrae />

            <Flex
              gap={isMobileBreakpoint ? "8px" : "16px"}
              overflow="hidden"
              justifyContent="center"
              maxHeight="200px"
              maxWidth="100%"
            >
              <Image
                borderRadius="16px"
                border="8px solid rgba(255, 255, 255, 0.08)"
                src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/portrait1_v2.jpg`}
                alt="Valkyrae portrait photo - content creator and gaming personality"
                height={isMobileBreakpoint ? "100px" : "165px"}
                objectFit="cover"
                objectPosition="top"
                aspectRatio="134/165"
              />
              <Image
                borderRadius="16px"
                border="8px solid rgba(255, 255, 255, 0.08)"
                src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/portrait2_v2.jpg`}
                alt="Valkyrae in casual setting - showcasing her personality"
                height={isMobileBreakpoint ? "100px" : "165px"}
                objectFit="cover"
                objectPosition="top"
                aspectRatio="222/165"
              />
              <Image
                borderRadius="16px"
                border="8px solid rgba(255, 255, 255, 0.08)"
                src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/portrait3_v2.jpg`}
                alt="Valkyrae professional headshot - content creator and comic collaborator"
                height={isMobileBreakpoint ? "100px" : "165px"}
                objectFit="cover"
                objectPosition="top"
                aspectRatio="154/165"
              />
            </Flex>
            <Box width="216px">
              <SocialMediaIcons mediaLinks={socialMediaLinks} />
            </Box>
          </Flex>
          <Apollyon comicData={comicData} />
          <Flex
            marginTop="-48px"
            as="section"
            flexDirection="column"
            gap="40px"
            width="100%"
            alignItems="center"
            aria-labelledby="apollyon-section"
          >
            <Creators />
          </Flex>
          <Box
            as="section"
            width="100%"
            aria-labelledby="recommendations-heading"
          >
            <Reccomendations
              sectionSlug={
                import.meta.env.PROD
                  ? "739eaa64-2f84-44e2-8a40-bcfd982eb078"
                  : "977f1b21-5e2a-11f0-bc49-0242ac110002"
              }
              handleCta={focusPayment}
            />
          </Box>

          <HiHiStudios artistData={comicData?.artist} />

          <Flex
            ref={paymentRef as React.RefObject<HTMLDivElement>}
            width="100%"
          >
            {shouldHideFunnel || !comicData ? null : (
              <Flex
                data-payment-section
                padding={isSmallBreakpoint ? "32px 24px" : "64px 32px"}
                background="charcoal.700"
                borderRadius="24px"
                width="100%"
                justifyContent="center"
              >
                <Flex maxWidth="512px" width="100%" justifyContent="center">
                  <LandingPayment
                    contextArea={FunnelContextArea.valkyrae}
                    title={
                      <>
                        Become a Gold Member to access{" "}
                        <span
                          style={{
                            transform: "skew(-12deg)",
                            display: "inline-block",
                          }}
                        >
                          Apollyon
                        </span>{" "}
                        and over 100,000 titles.
                      </>
                    }
                    triggerFocusPayment={triggerFocusPayment}
                    comic={comicData}
                    activeFunnel={activeFunnel}
                    setActiveFunnel={setActiveFunnel}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
          <ValkyraeSubscribeBanner
            isHidden={!showSubscribeBanner || !isSmallBreakpoint}
            onSubscribeClick={focusPayment}
          />
          <ValkyraeFooter isMobileBreakpoint={isMobileBreakpoint} />
        </Flex>
      </Flex>
    </Flex>
  );
}

export function LandingPayment({
  comic,
  contextArea,
  title,
  triggerFocusPayment = 0,
  activeFunnel,
  setActiveFunnel,
}: {
  comic: Comic;
  contextArea: FunnelContextArea;
  title?: string | JSX.Element;
  triggerFocusPayment?: number;
  activeFunnel: FUNNEL_TYPES | undefined;
  setActiveFunnel: (type: FUNNEL_TYPES | undefined) => void;
}) {
  const accountData = useAccountData();

  // Reset funnel when user logs out
  useEffect(() => {
    if (!accountData?.user) {
      setActiveFunnel(FUNNEL_TYPES.subscription);
    }
  }, [accountData?.user, setActiveFunnel]);

  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.valkyraePage,
  });
  const { goToCTAUrl } = useNavigateToReaderWithContext({
    area: contextArea,
    comicToNavigate: comic,
    trigger: NavigationTrigger.congratsSplashCTAButton,
  });

  const propsToPass = {
    displayExploreMoreContent: true,
    hidePromoCode: true,
    hideValueProps: true,
    customHeader: title,
    centerWelcomeText: true,
    showEmailPasswordForm: true,
    triggerFocusPayment,
    largeLoginCTA: true,
    customCongratsImage: `${window.gc.global.assets_url}/static/images/web-ui/valkyrae/valkyraeCongrats.png`,
  };

  const { funnelBody } = useContentFunnel({
    setFunnelConfig: (funnelConfig) => setActiveFunnel(funnelConfig?.type),
    onCloseFunnel: () => setActiveFunnel(undefined),
    openFunnelType: activeFunnel,
    context: { modalContext: FunnelContextArea.valkyrae, navigationContext },
    options: {
      componentProps: propsToPass,
      onSuccess: goToCTAUrl,
    },
  });

  return funnelBody;
}
